import { CaretRight, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { Icon } from '../atoms/Icon';
import { cardSurface } from '../lib/card';
import { cn } from '../lib/cn';
import { uiText } from '../i18n';

export interface WeatherHour {
  time: string;
  temp: number;
  icon: PhosphorIcon;
  /** Chance of rain 0–100. */
  rain: number;
}

export interface WeatherCardProps {
  place: string;
  temp: number;
  icon: PhosphorIcon;
  summary: string;
  /** Practical advice, e.g. "Schirm einpacken". */
  hint?: string;
  hours: WeatherHour[];
  /** strip = one slim row for the dashboard (hours only where there is room). */
  variant?: 'card' | 'strip';
  /** Makes the whole strip a button that opens the details (forecast). */
  onOpen?: () => void;
  className?: string;
}

/**
 * Weather as a picture of the rest of the day: temperature curve, rain bars, one clear tip.
 * The numbers stay readable for screen readers; the drawing is decorative.
 */
export function WeatherCard({
  place,
  temp,
  icon,
  summary,
  hint,
  hours,
  variant = 'card',
  onOpen,
  className,
}: WeatherCardProps) {
  if (variant === 'strip') {
    return (
      <section
        aria-label={uiText().weatherIn(place)}
        className={cn(
          '@container relative flex items-center gap-gap-sm rounded-card px-inset-sm py-inset-xs',
          cardSurface,
          className,
        )}
      >
        {onOpen && (
          // stretched button: the whole strip is the touch target, the content stays readable text
          <button
            type="button"
            onClick={onOpen}
            aria-label={uiText().weatherDetails}
            className="absolute inset-0 z-10 cursor-pointer rounded-card outline-none focus-visible:outline-3 focus-visible:outline-border-focus"
          />
        )}
        <Icon icon={icon} size="lg" weight="duotone" className="shrink-0 text-accent-tertiary" />
        <span className="font-display text-2xl font-black tabular-nums">{temp}°</span>
        <span className="min-w-0 truncate text-sm text-text-secondary">{summary}</span>
        <ol
          className="ml-auto hidden items-center gap-gap-sm @narrow:flex"
          aria-label={uiText().weatherCourse}
        >
          {hours.map((h) => (
            <li key={h.time} className="flex flex-col items-center text-xs tabular-nums">
              <span className="text-text-muted">
                <span aria-hidden>{h.time.slice(0, 2)}</span>
                <span className="sr-only">{uiText().hour(h.time)}</span>
              </span>
              <Icon icon={h.icon} size="sm" className="text-text-secondary" />
              <span className="font-bold">{h.temp}°</span>
              <span className="sr-only">{uiText().rainPercent(String(h.rain))}</span>
            </li>
          ))}
        </ol>
        {hint && (
          <span className="ml-auto shrink-0 rounded-chip bg-accent-secondary-soft px-inset-xs py-1 text-xs font-bold @narrow:ml-0">
            {hint}
          </span>
        )}
        {onOpen && <Icon icon={CaretRight} size="sm" className="shrink-0 text-text-muted" />}
      </section>
    );
  }

  const temps = hours.map((h) => h.temp);
  const min = Math.min(...temps) - 1;
  const max = Math.max(...temps) + 1;
  const n = hours.length;
  const x = (i: number) => ((i + 0.5) / n) * 100;
  const y = (t: number) => 28 - ((t - min) / (max - min || 1)) * 22;
  const line = hours.map((h, i) => `${x(i)},${y(h.temp)}`).join(' ');

  return (
    <section
      aria-label={uiText().weatherIn(place)}
      className={cn('flex flex-col gap-gap-md rounded-card p-inset-md', cardSurface, className)}
    >
      <div className="flex items-center gap-gap-md">
        <Icon icon={icon} size="xl" weight="duotone" className="text-accent-tertiary" />
        <div className="flex flex-col">
          <span className="font-display text-3xl font-black leading-none tabular-nums">
            {temp}°
          </span>
          <span className="text-sm text-text-secondary">{summary}</span>
        </div>
        {hint && (
          <span className="ml-auto rounded-chip bg-accent-secondary-soft px-inset-sm py-1 text-sm font-bold">
            {hint}
          </span>
        )}
      </div>

      <div className="relative">
        <svg
          viewBox="0 0 100 30"
          preserveAspectRatio="none"
          aria-hidden
          className="h-10 w-full overflow-visible"
        >
          <polyline
            points={line}
            fill="none"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
            className="stroke-reward-points"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        <ol className="grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
          {hours.map((h) => (
            <li key={h.time} className="flex flex-col items-center gap-1 text-center">
              <span className="text-sm font-bold tabular-nums">{h.temp}°</span>
              <Icon icon={h.icon} size="md" className="text-text-secondary" />
              <span
                aria-hidden
                className="flex h-6 w-2 items-end overflow-hidden rounded-chip bg-surface-sunken"
              >
                <span
                  className="w-full rounded-chip bg-accent-secondary"
                  style={{ height: `${h.rain}%` }}
                />
              </span>
              {/* Hour only ("22"): full times do not fit six columns on a phone. */}
              <span className="text-xs text-text-muted tabular-nums">
                <span aria-hidden>{h.time.slice(0, 2)}</span>
                <span className="sr-only">{uiText().hour(h.time)}</span>
              </span>
              <span className="sr-only">{uiText().rainPercent(String(h.rain))}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
